/**
 * Maps route paths to human-readable page names
 * @param {string} pathname - The current route pathname
 * @param {function} gettext - Translation function
 * @returns {string} Human-readable page name
 */
export const getPageNameFromPath = (
  pathname: string,
  gettext: (key: string) => string,
) => {
  // Remove leading slash and split path segments
  const segments = pathname.replace(/^\//, "").split("/");

  // Define route mappings
  const routeMap: Record<string, string> = {
    // Auth pages
    "auth/login": gettext("Login"),
    "auth/register": gettext("Register"),
    "auth/forgot-password": gettext("Forgot Password"),
    "auth/reset-password": gettext("Reset Password"),

    // User pages
    "user-account/profile": gettext("Profile Settings"),
    "user-account": gettext("My Account"),

    // System admin pages
    "system-admin": gettext("System Administration"),
    "system-admin/users": gettext("Users"),
    "system-admin/partners": gettext("Partners"),
    "system-admin/companies": gettext("Companies"),

    // Partner admin pages
    "partner-admin": gettext("Partner Administration"),
    "partner-admin/settings": gettext("Partner Settings"),
    "partner-admin/users": gettext("Partner Users"),
    "partner-admin/tenants": gettext("Corporate Groups"),
    "partner-admin/companies": gettext("Partner Companies"),

    // Company admin pages
    "corporate-group": gettext("Company Administration"),
    "corporate-group/users": gettext("Company Users"),
    "corporate-group/companies": gettext("Companies"),

    // Energy pages
    "energy/consumption": gettext("Energy Consumption"),
    "energy/statement": gettext("Energy Statement"),
    "energy/carbon": gettext("Carbon Footprint"),
    "energy/climate": gettext("Climate Statement"),
    "energy/procurement": gettext("Energy Procurement"),
    "energy/prices": gettext("Energy Prices"),
    "energy/tariff": gettext("Tariff Statement"),
    "energy/spot-prices": gettext("Spot Prices"),
    "energy/invoice": gettext("Energy Invoice"),
    budget: gettext("Budget"),

    // Support pages
    support: gettext("Support"),

    // Developer pages
    developer: gettext("Developer Tools"),
  };

  // Try to match the full path
  const fullPath = segments.join("/");
  if (routeMap[fullPath]) {
    return routeMap[fullPath];
  }

  // Try to match partial paths (e.g., for dynamic routes)
  for (let i = segments.length; i > 0; i--) {
    const partialPath = segments.slice(0, i).join("/");
    if (routeMap[partialPath]) {
      return routeMap[partialPath];
    }
  }

  // Check for user profile specific pattern
  if (segments.includes("users") && segments.includes("profile")) {
    return gettext("User Profile");
  }

  if (
    (segments.includes("users") ||
      segments.includes("all-users") ||
      segments.includes("partner-users") ||
      segments.includes("company-users")) &&
    segments.includes("settings")
  ) {
    return gettext("User Settings");
  }

  if (
    (segments.includes("users") ||
      segments.includes("all-users") ||
      segments.includes("partner-users") ||
      segments.includes("company-users") ||
      segments.includes("user-account")) &&
    segments.includes("notifications")
  ) {
    const notifIndex = segments.indexOf("notifications");
    if (notifIndex !== -1 && segments.length > notifIndex + 1) {
      return gettext("Notification Details");
    }
    return gettext("User Notifications");
  }

  // Check for partner-specific routes
  if (segments.includes("partners") && segments.includes("tenants")) {
    return gettext("Corporate Groups");
  }

  if (segments.includes("partners") && segments.includes("users")) {
    return gettext("Partner Users");
  }

  if (segments.includes("partners") && segments.includes("settings")) {
    return gettext("Partner Settings");
  }

  // Check for tenant-specific routes
  if (segments.includes("tenants") && segments.includes("users")) {
    return gettext("Company Users");
  }

  if (segments.includes("tenants") && segments.includes("companies")) {
    return gettext("Companies");
  }

  if (segments.includes("tenants") && segments.includes("settings")) {
    return gettext("Corporate Group Settings");
  }

  // Fallback to capitalize the last segment
  const lastSegment = segments[segments.length - 1];
  if (lastSegment && lastSegment !== "") {
    return (
      lastSegment.charAt(0).toUpperCase() +
      lastSegment.slice(1).replace(/[-_]/g, " ")
    );
  }

  // Default fallback
  return gettext("Dashboard");
};
